import { gql, useMutation } from '@apollo/client'
import { ORDER } from '@hive/status'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'

import CancelOrderModal from '@entities/Order/ui/OrderOnline/CancelOrderModal'

import { useErrorInfoModal, useModalStore } from '@features/modal'

import { OrdersEdge } from '@shared/generated'
import Button from '@shared/ui/button'
import { formatPrice } from '@shared/uttils'
import { formatDate } from '@shared/uttils/formatDate'
import { getOrderStatusLabel } from '@shared/uttils/order'

import styles from './styles.module.scss'

const CANCEL_ORDER_MUTATION = gql`
  mutation CancelOrderManual($orderId: ID!) {
    cancelOrder(order_id: $orderId) {
      id
      status
    }
  }
`

type CancelOrderMutationResponse = {
  cancelOrder: {
    id: string;
    status: string;
  };
}

type CancelOrderMutationVariables = {
  orderId: string;
}

export default function OrderHeader ({
  data,
  isDetail,
  handleDetailClick,
  onOrderUpdated,
}: {
  data: NonNullable<NonNullable<OrdersEdge['edges']>[number]>;
  isDetail: boolean;
  handleDetailClick: () => void;
  onOrderUpdated?: () => Promise<unknown>;
}) {
  const { t: to } = useTranslation('order')
  const { openModal, closeModal } = useModalStore()
  const { openErrorInfoModal } = useErrorInfoModal()
  const [ cancelOrder, { loading: cancelOrderLoading } ] = useMutation<
    CancelOrderMutationResponse,
    CancelOrderMutationVariables
  >(CANCEL_ORDER_MUTATION)
  const { label, color } = getOrderStatusLabel(data.status, to)
  const canCancel = data.status === ORDER.CONFIRMED

  const handleCancelClick = async () => {
    if (!canCancel || !data.id) return

    const handleCancelOrder = async () => {
      try {
        await cancelOrder({
          variables: {
            orderId: data.id,
          },
        })

        closeModal('orderCancel')
        await onOrderUpdated?.()
      }
      catch (error) {
        console.error('Cancel order error:', error)
        closeModal('orderCancel')
        openErrorInfoModal({
          description: to('order.cancel.error'),
        })
      }
    }

    openModal(
      'orderCancel',
      <CancelOrderModal
        loading={cancelOrderLoading}
        onClose={() => closeModal('orderCancel')}
        onConfirm={handleCancelOrder}
      />,
      {
        title: to('order.cancel.title'),
        description: to('order.cancel.confirm'),
      },
    )
  }

  return (
    <div
      className={classNames(styles.header, {
        [styles['header--active']]: isDetail,
      })}
    >
      <div className={styles.header__order}>
        <div className={styles.header__title}>
          <h4 className={styles.header__titleLine}>
            <span>
              {to('order')} <span data-green>№ {data.id}</span>
            </span>
            {canCancel && (
              <Button
                size="small"
                theme="grey"
                onClick={handleCancelClick}
                disabled={cancelOrderLoading}
              >
                {to('order.button.cancel')}
              </Button>
            )}
          </h4>
          {data.deliveryClaim?.packagePickupCode && data.status !== ORDER.DELIVERED && (
            <p className={styles.header__pickupCode}>
              {'• '}
              {to('order.pickup-code')}: {data.deliveryClaim.packagePickupCode}
            </p>
          )}
          <span> {formatDate(data?.created_at)}</span>
        </div>
        <div className={styles.header__control}>
          {!isDetail && <h3> {formatPrice(data.total as number)}</h3>}
          <span data-color={color} className={styles.header__status}>
            {label}
          </span>
        </div>
        <Button
          onClick={handleDetailClick}
          className={classNames(styles.header__button, {
            [styles['header__button--active']]: isDetail,
          })}
          theme="grey"
          size="small"
        >
          <ReactSVG src="/icons/arrow-right.svg" wrapper="span" />
        </Button>
      </div>

      {isDetail && (
        <>
          <div className={styles.header__data}>
            {data.recipientInfo?.firstName && (
              <InfoRow
                label={to('order.user')}
                value={data.recipientInfo?.firstName as string}
              />
            )}

            <InfoRow
              label={to('order.address')}
              value={data.address as string}
            />
            <InfoRow
              label={to('order.time-delivery')}
              value={data.deliverySlot?.slotTime || ''}
              isHighlight
            />
            {data.paymentMethod && (
              <InfoRow
                label={to('order.payment-variant')}
                value={`${to('card')} ****  ${data.paymentMethod?.last || ''}`}
                isHighlight
              />
            )}
          </div>
          {/* <div className={styles.header__addition}>
            <p
              dangerouslySetInnerHTML={{
                __html: to("order.add-delivery") + " 7:53",
              }}
            ></p>
            <Button size="medium"> {to("order.button.catalog")}</Button>
          </div> */}
        </>
      )}
    </div>
  )
}

function InfoRow ({
  label,
  value,
  isHighlight = false,
}: {
  label: string;
  value: string;
  isHighlight?: boolean;
}) {
  return (
    <p>
      {label}: <span data-green={isHighlight ? true : undefined}>{value}</span>
    </p>
  )
}
